// Transactional receipt + allocation service (Stage 2). Generic across all 8
// client flows because it reads/writes off approved_invoice_snapshots
// (Stage 1's universal source of truth) rather than any per-client registry
// — this is additive to, and does not modify, Corteva's/IIL's existing
// bespoke reconciliation pages (CortevaReconciliation.js/IILReconciliation.js).
import {
  collection, doc, getDocs, query, runTransaction, serverTimestamp, where
} from "firebase/firestore";
import { auth, db } from "../../firebase";
import { allocateProportionally } from "./receivablesCalculator";
import { allocationShortfalls, splitAdjustment, validateAdjustment } from "./receiptShortfall";
import { ensureWhtControlRecordInTransaction, recognizeWhtDeductionInTransaction } from "./whtControlService";
import { assertPeriodOpen } from "./periodLockService";
import { deductionProblem } from "./deductionLimits";
import { isVoided } from "./voidedInvoice";

const RECEIPTS = "client_receipts";
const REFERENCE_LOCKS = "client_receipts_by_reference";
const ALLOCATIONS = "receipt_allocations";

function normalizeReferenceKey(clientCode, bankReference) {
  return `${clientCode}_${String(bankReference || "").trim().toLowerCase()}`.replace(/[/\s]+/g, "_");
}

export async function listReceiptsForClient(clientCode) {
  const q = query(collection(db, RECEIPTS), where("clientCode", "==", clientCode));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// selections: [{ snapshotId, invoiceNumber, expectedReceipt, whtRecognized, gstWithheldRecognized, otherDeductionsRecognized }]
// allocationMethod PROPORTIONAL derives allocatedAmount from expectedReceipt (allocateProportionally);
// MANUAL requires the caller to already have set allocatedAmount on each selection.
export async function createReceiptWithAllocations({
  clientCode, receiptDate, bankReference, receivedAmount, notes, selections, allocationMethod, adjustment
}) {
  const trimmedReference = String(bankReference || "").trim();
  if (!clientCode) throw new Error("A client is required.");
  if (!receiptDate) throw new Error("Enter the receipt date.");
  if (!trimmedReference) throw new Error("Enter the bank reference.");
  if (!(Number(receivedAmount) > 0)) throw new Error("Enter a received amount greater than zero.");
  if (!Array.isArray(selections) || !selections.length) throw new Error("Select at least one invoice to allocate this receipt against.");

  const amountRounded = Math.round(Number(receivedAmount));
  const allocated = allocationMethod === "MANUAL"
    ? selections
    : allocateProportionally(amountRounded, selections);

  const totalAllocated = allocated.reduce((sum, s) => sum + Number(s.allocatedAmount || 0), 0);
  if (Math.round(totalAllocated) !== amountRounded) {
    throw new Error(`Allocations (PKR ${totalAllocated}) must add up to the received amount (PKR ${amountRounded}).`);
  }

  // A short receipt can be closed off with a client adjustment: the difference is
  // recognised against the invoices (so they stop showing as owed) and booked as a
  // direct cost of this client. Only what is actually short can be adjusted.
  let adjustmentShares = allocated.map(() => 0);
  let adjustmentAmount = 0;
  if (adjustment && Math.round(Number(adjustment.amount || 0)) > 0) {
    const shortfalls = allocationShortfalls(allocated);
    const problem = validateAdjustment({
      amount: adjustment.amount, note: adjustment.note, accountingPeriod: adjustment.accountingPeriod,
      shortfall: shortfalls.reduce((sum, value) => sum + value, 0)
    });
    if (problem) throw new Error(problem);
    await assertPeriodOpen(adjustment.accountingPeriod);
    adjustmentShares = splitAdjustment(adjustment.amount, shortfalls);
    adjustmentAmount = Math.round(Number(adjustment.amount));
  }

  // What earlier receipts already recognised against each invoice, so the WHT / GST
  // withheld on this one can be checked against what the invoice still allows.
  const priorAllocations = await Promise.all(allocated.map((selection) =>
    getDocs(query(collection(db, ALLOCATIONS), where("snapshotId", "==", selection.snapshotId)))
  ));
  const priorRecognised = priorAllocations.map((result) => result.docs.map((d) => d.data()).filter((a) => !a.reversed).reduce(
    (sum, a) => ({ wht: sum.wht + Number(a.whtRecognized || 0), gst: sum.gst + Number(a.gstWithheldRecognized || 0) }),
    { wht: 0, gst: 0 }
  ));

  const referenceKey = normalizeReferenceKey(clientCode, trimmedReference);
  const receiptRef = doc(collection(db, RECEIPTS));
  const referenceLockRef = doc(db, REFERENCE_LOCKS, referenceKey);
  const directCostRef = adjustmentAmount > 0 ? doc(collection(db, "client_direct_costs")) : null;

  await runTransaction(db, async (transaction) => {
    const lockSnapshot = await transaction.get(referenceLockRef);
    if (lockSnapshot.exists()) {
      throw new Error(`Bank reference "${trimmedReference}" has already been recorded for ${clientCode}.`);
    }

    // Firestore transactions require every read before any write — read all
    // affected WHT control records up front via ensureWhtControlRecordInTransaction's
    // read half before issuing any transaction.set() calls below.
    const controlLookups = await Promise.all(
      allocated.map((selection) => ensureWhtControlRecordInTransaction(transaction, selection.snapshotId))
    );
    const invoiceSnapshots = await Promise.all(
      allocated.map((selection) => transaction.get(doc(db, "approved_invoice_snapshots", selection.snapshotId)))
    );
    allocated.forEach((selection, index) => {
      const invoice = invoiceSnapshots[index];
      if (!invoice.exists()) throw new Error(`Invoice ${selection.invoiceNumber} was not found.`);
      if (isVoided(invoice.data())) throw new Error(`Invoice ${selection.invoiceNumber} has been voided and cannot receive a receipt.`);
      const totals = invoice.data().totals || {};
      const problem = deductionProblem({
        invoiceNumber: selection.invoiceNumber,
        expectedWht: totals.whtAmount, expectedGst: totals.gstWithheldAmount,
        priorWht: priorRecognised[index].wht, priorGst: priorRecognised[index].gst,
        wht: selection.whtRecognized, gst: selection.gstWithheldRecognized,
        overrideReason: selection.deductionOverrideReason
      });
      if (problem) throw new Error(problem);
    });

    const timestamp = serverTimestamp();
    transaction.set(receiptRef, {
      clientCode,
      receiptDate,
      bankReference: trimmedReference,
      receivedAmount: amountRounded,
      allocatedAmount: amountRounded,
      unallocatedAmount: 0,
      notes: String(notes || "").trim(),
      ...(adjustmentAmount > 0 ? {
        adjustmentAmount,
        adjustmentNote: String(adjustment.note).trim(),
        adjustmentAccountingPeriod: adjustment.accountingPeriod,
        adjustmentDirectCostId: directCostRef.id
      } : {}),
      enteredByUid: auth.currentUser?.uid || null,
      enteredByEmail: auth.currentUser?.email || null,
      enteredAt: timestamp,
      reversed: false
    });
    if (directCostRef) {
      transaction.set(directCostRef, {
        clientCode,
        accountingPeriod: adjustment.accountingPeriod,
        category: "CLIENT_ADJUSTMENT",
        amount: adjustmentAmount,
        description: `Client adjustment on short receipt ${trimmedReference}: ${String(adjustment.note).trim()} (invoices: ${allocated.map((row) => row.invoiceNumber).join(", ")})`,
        paymentDate: receiptDate,
        referenceNumber: trimmedReference,
        source: "RECEIPT_ADJUSTMENT",
        sourceReceiptId: receiptRef.id,
        reversed: false,
        enteredByUid: auth.currentUser?.uid || null,
        enteredByEmail: auth.currentUser?.email || null,
        enteredAt: timestamp
      });
    }
    transaction.set(referenceLockRef, {
      receiptId: receiptRef.id, clientCode, bankReference: trimmedReference, createdAt: timestamp
    });

    allocated.forEach((selection, index) => {
      const allocationRef = doc(collection(db, ALLOCATIONS));
      const whtRecognized = Number(selection.whtRecognized || 0);
      const gstWithheldRecognized = Number(selection.gstWithheldRecognized || 0);
      transaction.set(allocationRef, {
        receiptId: receiptRef.id,
        clientCode,
        snapshotId: selection.snapshotId,
        invoiceNumber: selection.invoiceNumber,
        allocationMethod: allocationMethod === "MANUAL" ? "MANUAL" : "PROPORTIONAL",
        allocatedAmount: Number(selection.allocatedAmount || 0),
        whtRecognized,
        gstWithheldRecognized,
        otherDeductionsRecognized: Number(selection.otherDeductionsRecognized || 0) + adjustmentShares[index],
        ...(String(selection.deductionOverrideReason || "").trim() ? { deductionOverrideReason: String(selection.deductionOverrideReason).trim() } : {}),
        enteredByUid: auth.currentUser?.uid || null,
        enteredByEmail: auth.currentUser?.email || null,
        createdAt: timestamp,
        reversed: false
      });

      if (whtRecognized > 0) {
        recognizeWhtDeductionInTransaction(transaction, controlLookups[index], whtRecognized);
      }
    });
  });

  return { receiptId: receiptRef.id, allocated, adjustmentAmount };
}

// Reversal preserves history (requirement 10/28) — flags the receipt and its
// allocations as reversed rather than deleting them, and unwinds any WHT
// recognized against the control record. The bank-reference lock is left in
// place even after reversal so the same reference can't be silently reused.
export async function reverseReceipt(receiptId, reason) {
  if (!String(reason || "").trim()) throw new Error("Enter a reason for reversing this receipt.");

  const allocationsQuery = query(collection(db, ALLOCATIONS), where("receiptId", "==", receiptId));
  const allocationsSnapshot = await getDocs(allocationsQuery);
  const allocationDocs = allocationsSnapshot.docs.filter((d) => !d.data().reversed);

  await runTransaction(db, async (transaction) => {
    const receiptRef = doc(db, RECEIPTS, receiptId);
    const receiptSnapshot = await transaction.get(receiptRef);
    if (!receiptSnapshot.exists()) throw new Error("Receipt not found.");
    if (receiptSnapshot.data().reversed) throw new Error("This receipt was already reversed.");

    const controlLookups = await Promise.all(
      allocationDocs.map((d) => ensureWhtControlRecordInTransaction(transaction, d.data().snapshotId))
    );

    const timestamp = serverTimestamp();
    const adjustmentCostId = receiptSnapshot.data().adjustmentDirectCostId;
    if (adjustmentCostId) {
      await assertPeriodOpen(receiptSnapshot.data().adjustmentAccountingPeriod);
      transaction.update(doc(db, "client_direct_costs", adjustmentCostId), {
        reversed: true,
        reversalReason: `Receipt ${receiptSnapshot.data().bankReference} reversed: ${String(reason).trim()}`,
        reversedAt: timestamp,
        reversedByEmail: auth.currentUser?.email || null
      });
    }
    transaction.update(receiptRef, {
      reversed: true,
      reversedBy: auth.currentUser?.uid || null,
      reversedByEmail: auth.currentUser?.email || null,
      reversalReason: String(reason).trim(),
      reversedAt: timestamp,
      unallocatedAmount: receiptSnapshot.data().receivedAmount
    });

    allocationDocs.forEach((allocationDoc, index) => {
      const data = allocationDoc.data();
      transaction.update(allocationDoc.ref, {
        reversed: true,
        reversedBy: auth.currentUser?.uid || null,
        reversedByEmail: auth.currentUser?.email || null,
        reversalReason: String(reason).trim(),
        reversedAt: timestamp
      });
      if (Number(data.whtRecognized || 0) > 0) {
        recognizeWhtDeductionInTransaction(transaction, controlLookups[index], -Number(data.whtRecognized));
      }
    });
  });
}
